import { AppService } from '@/bundles/App/models/AppService';
import { IPaginationResponse } from '@/bundles/App/interfaces/IPaginationResponse';
import AvailabilityIngestion from '@/bundles/AvailabilityIngestion/models/AvailabilityIngestion';
import { toApproveError } from '@/bundles/AvailabilityIngestion/helpers/approveError';
import {
  IAvailabilityIngestion,
  IListFilters,
  IUploadAvailabilityFormFields,
  IPatchExtractionRequest,
  IRejectAvailabilityRequest,
  IApproveAvailabilityRequest,
} from '@/bundles/AvailabilityIngestion/types';

// All routes are account-scoped under `/availability-ingestion`; the list spans
// every property by default (narrow with `filters.property_id`), and each record
// carries `property_id` so a row can navigate to its detail.
const baseUrl = '/availability-ingestion';

export default class AvailabilityIngestionService extends AppService {
  async list (filters: IListFilters = {}): Promise<IPaginationResponse<IAvailabilityIngestion>> {
    try {
      const response = await this.transport.get(baseUrl, filters);

      return {
        data: response.data.map((item: IAvailabilityIngestion) => new AvailabilityIngestion(item)),
        total: response.total,
      };
    } catch (error) {
      throw this.errorMapper.mapToCodeModel(error);
    }
  }

  async one (id: string): Promise<IAvailabilityIngestion> {
    try {
      const response = await this.transport.get(`${baseUrl}/${id}`);

      return new AvailabilityIngestion(response);
    } catch (error) {
      throw this.errorMapper.mapToCodeModel(error);
    }
  }

  async upload (propertyId: string, fields: IUploadAvailabilityFormFields): Promise<IAvailabilityIngestion> {
    try {
      const formData = new FormData();
      formData.append('file', fields.file, fields.file.name);
      formData.append('is_create_attachment', 'false');

      if (fields.extraction_model) {
        formData.append('extraction_model', fields.extraction_model);
      }

      const response = await this.transport.post(`${baseUrl}/${propertyId}`, formData);

      return new AvailabilityIngestion(response);
    } catch (error) {
      throw this.errorMapper.mapToCodeModel(error);
    }
  }

  async patchExtraction (id: string, body: IPatchExtractionRequest): Promise<IAvailabilityIngestion> {
    try {
      const response = await this.transport.patch(`${baseUrl}/${id}/extraction`, body);

      return new AvailabilityIngestion(response);
    } catch (error) {
      throw this.errorMapper.mapToCodeModel(error);
    }
  }

  async reject (id: string, body: IRejectAvailabilityRequest): Promise<IAvailabilityIngestion> {
    try {
      const response = await this.transport.post(`${baseUrl}/${id}/reject`, body);

      return new AvailabilityIngestion(response);
    } catch (error) {
      throw this.errorMapper.mapToCodeModel(error);
    }
  }

  async approve (id: string, body: IApproveAvailabilityRequest): Promise<IAvailabilityIngestion> {
    try {
      const response = await this.transport.post(`${baseUrl}/${id}/approve`, body);

      return new AvailabilityIngestion(response);
    } catch (error) {
      // `errorMapper.mapToCodeModel` keeps only message/title/status/url — that would
      // discard the 409 candidate list / 400 missing-fields body this flow needs.
      throw toApproveError(error);
    }
  }
}
